import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, DialogProps, Typography } from "@mui/material";
import { deleteProductChangelog } from "@products/store/index";
import { PermissionService } from "@services/permissionService/permissionService";
import SemverService from "@services/semverService/semverService";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme) => ({
    versionBox: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.emteria.background.paper,
    },
    topDistance: {
        marginTop: theme.spacing(3),
    },
    changeList: {
        paddingLeft: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    title: {
        paddingBottom: 0,
    },
    version: {
        fontWeight: "bold",
    },
    versionHeader: {
        position: "relative",
        marginBottom: theme.spacing(1),
    },
    versionTitle: {
        fontWeight: "bold",
    },
    date: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.5),
        fontWeight: 500,
    },
    dialogActions: {
        justifyContent: "space-between",
        padding: theme.spacing(6),
    },
    dialogActionsFlexEnd: {
        justifyContent: "flex-end",
    },
    editBtn: {
        alignSelf: "flex-end",
    },
    deleteButtonBox: {
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        paddingRight: theme.spacing(5),
    },
    deleteButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(3),
    },
    grow: {
        flexGrow: 1,
    },
    hidden: {
        display: "none",
    },
}));

interface ChangeLinesProps {
    change: string;
}
const ChangeLines = (props: ChangeLinesProps) => (
    <Box>
        {props.change.split("\n").map((line, lineIndex) => (
            <Typography key={lineIndex} variant="body1">
                {line}
            </Typography>
        ))}
    </Box>
);

interface VersionChangesProps {
    version: string;
    date: string;
    isEditing: boolean;
    changesList: string[];
    productId?: number;
}
const VersionChanges = (props: VersionChangesProps) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["modals", "common"]);
    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const handleDelete = async () => {
        if (props.productId) {
            try {
                await dispatch(deleteProductChangelog({ productId: props.productId, version: props.version })).unwrap();
                showSuccessToast(t("modals:changesDialog.toast.deletedChangelogSuccess"));
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("modals:changesDialog.toast.deletedChangelogFailed"));
            }
        }
    };

    return (
        <Box key={props.version} className={classes.versionBox}>
            <Box className={classes.deleteButtonBox}>
                <Box className={classes.versionHeader}>
                    <Typography className={classes.versionTitle}>
                        {t("common:label.version")} {props.version}
                    </Typography>
                </Box>
                <Typography variant="caption" className={classes.date}>
                    {props.date}
                </Typography>
                {props.isEditing ? (
                    <DangerOutlinedButton
                        onClick={handleDelete}
                        className={classes.deleteButton}
                        disabled={cannotManageProducts}
                        tooltipProps={{
                            title: cannotManageProducts
                                ? t("modals:changesDialog.tooltips.wrongRole")
                                : t("modals:changesDialog.tooltips.deleteChangelog"),
                            placement: "top",
                        }}
                    >
                        <i className="fa-solid fa-trash-alt" />
                    </DangerOutlinedButton>
                ) : null}
            </Box>
            <Box className={classes.changeList}>
                {props.changesList.length > 0 ? (
                    props.changesList.map((change, index) => <ChangeLines key={index} change={change} />)
                ) : (
                    <Typography variant="body1">{t("modals:changesDialog.noChanges")}</Typography>
                )}
            </Box>
        </Box>
    );
};

interface ChangesListProps {
    isEditing: boolean;
    changesWithDate: {
        version: string;
        date: string;
        changes: string[];
    }[];
    productId?: number;
}
const ChangesList: React.FC<ChangesListProps> = (props: ChangesListProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);

    const sortedChanges = [...props.changesWithDate].sort((a, b) => SemverService.compare(b.version, a.version));

    return (
        <Box className={classes.topDistance}>
            {sortedChanges.length > 0 ? (
                sortedChanges.map((versionData) => (
                    <VersionChanges
                        isEditing={props.isEditing}
                        key={versionData.version}
                        version={versionData.version}
                        date={versionData.date}
                        changesList={versionData.changes}
                        productId={props.productId}
                    />
                ))
            ) : (
                <Typography variant="body1">{t("modals:changesDialog.noReleaseNotes")}</Typography>
            )}
        </Box>
    );
};

interface ChangesDialogProps extends Omit<DialogProps, "onClose"> {
    onClose: () => void;
    title: string;
    changesWithDate:
        | {
              version: string;
              date: string;
              changes: string[];
          }[]
        | null;
    productId?: number;
}
export const ChangesDialog = ({ productId, changesWithDate, title, ...props }: ChangesDialogProps) => {
    const { classes, cx } = useStyles();
    const [isEditing, setIsEditing] = useState(false);
    const { t } = useTranslation(["modals"]);

    const handleEdit = () => {
        setIsEditing((prev) => !prev);
    };
    const handleOnClose = () => {
        props.onClose();
        setIsEditing(false);
    };

    return (
        <Modal
            {...props}
            title={title}
            onClose={handleOnClose}
            endButton={
                productId &&
                changesWithDate &&
                changesWithDate.length > 0 && (
                    <SecondaryButton
                        onClick={handleEdit}
                        className={cx(classes.editBtn, !changesWithDate.length && classes.hidden)}
                        tooltipProps={{
                            title: t("modals:changesDialog.manageVersionChangelogs"),
                        }}
                    >
                        <i className="fa-solid fa-cogs" />
                    </SecondaryButton>
                )
            }
        >
            <ChangesList isEditing={isEditing} changesWithDate={changesWithDate || []} productId={productId} />
        </Modal>
    );
};
